import { useForm } from '@tanstack/react-form'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useAddMemberMutation, useUpdateMemberMutation } from '../hooks'
import { MemberCreateSchema, MemberUpdateSchema } from '../schema/memberSchema'
import { memberRoles } from '../types/member'
import { FamilyMembersSelector } from './FamilyMembersSelector'
import type { ApiError } from '@/types/api'
import type { MemberCreateData, MemberUpdateData } from '../schema/memberSchema'
import type {
  FamilyRelation,
  MemberRole,
  MemberUpdateInput,
} from '../types/member'
import { AddressInput } from '@/components/custom/address-input'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Modal } from '@/components/custom/modal'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import { PhoneInput } from '@/components/custom/phone-input'
import { Switch } from '@/components/custom/switch'
import { Button } from '@/components/ui/button'
import { ChurchForm } from '@/features/churches/components/ChurchForm'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
// import { useEmailValidation } from '@/hooks'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { usePermissions } from '@/utils/permissions'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { EmailSchema } from '@/schemas'

const NULLISH_EMAIL = '' as string | null

const addDefaultValues = {
  name: '',
  address: { street: '', city: '', province: '', postal: '' },
  phone: '',
  email: '',
  etransferEmail: NULLISH_EMAIL,
  noEmail: false,
  familyMembers: [],
  churchId: '',
  role: 'MEMBER' as MemberRole,
  active: true,
}

export function MemberForm({
  id,
  type,
  trigger,
  open,
  onClose,
}: {
  id?: string
  type: 'add' | 'edit'
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}) {
  const { mutateAsync: addMemberMutation, isPending: isAdding } =
    useAddMemberMutation()
  const { mutateAsync: updateMemberMutation, isPending: isUpdating } =
    useUpdateMemberMutation()
  // const emailValidator = useEmailValidation()

  const [openChurchDialog, setOpenChurchDialog] = useState(false)

  const members = useMembersStore((s) => s.members)
  const initial = members.find((m) => m._id === id)

  const familyMembers = members
    .filter((m) => m._id !== id)
    .sort((a, b) => a.name.localeCompare(b.name))
  const churches = useChurchesStore((s) => s.churches)

  // Get permissions for church filtering
  const permissions = usePermissions()

  // Filter churches based on user's role and assignments
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredChurches =
    assignedChurchIds.length === 0
      ? churches.filter((church) => church.active) // Super admin sees all churches
      : churches.filter(
          (church) => church.active && assignedChurchIds.includes(church._id),
        )

  const initialFamilyMembers = Array.isArray(initial?.familyMembers)
    ? (initial.familyMembers as Array<
        string | { memberId: string; relation: FamilyRelation }
      >)
    : null
  const normalizedInitialFamilyMembers = initialFamilyMembers
    ? initialFamilyMembers.map((fm) =>
        typeof fm === 'string'
          ? { memberId: fm, relation: 'OTHER' as FamilyRelation }
          : fm,
      )
    : []

  const form = useForm({
    defaultValues:
      type === 'edit'
        ? {
            _id: initial?._id || '',
            name: initial?.name || '',
            address: initial?.address || {
              street: '',
              city: '',
              province: '',
              postal: '',
            },
            phone: initial?.phone || '',
            email: initial?.email || '',
            etransferEmail: initial?.etransferEmail ?? NULLISH_EMAIL,
            noEmail: initial?.noEmail || false,
            familyMembers: normalizedInitialFamilyMembers,
            churchId: initial?.churchId || '',
            role: initial?.role || 'MEMBER',
            active: initial?.active ?? false,
          }
        : addDefaultValues,
    onSubmit: async ({ value }) => {
      try {
        if (type === 'edit') {
          await updateMemberMutation({
            ...value,
            active: (value as MemberUpdateInput).active,
          } as MemberUpdateData)
        } else {
          await addMemberMutation({
            ...value,
            role: value.role,
          } as MemberCreateData)
        }
        onClose?.()
        form.reset()
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting user form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  const schema = type === 'edit' ? MemberUpdateSchema : MemberCreateSchema

  const isPending = isAdding || isUpdating
  const buttonLabel = type === 'add' ? 'Add' : 'Update'
  const loadingLabel = type === 'add' ? 'Adding...' : 'Updating...'

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={type === 'edit' ? 'Edit Member' : 'Add Member'}
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <form
        id="member-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2"
      >
        <form.Field
          name="name"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.name.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Name"
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  required
                />
              </div>
            )
          }}
        />
        <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full">
          <div className="flex-1 min-w-0">
            <form.Field
              name="email"
              validators={{
                onBlur: ({ value, fieldApi }) => {
                  const noEmail = fieldApi.form.getFieldValue('noEmail')

                  if (noEmail) return undefined

                  if (!value || value.trim() === '') {
                    return 'Email is required'
                  }

                  const result = EmailSchema.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
                // onBlurAsync: async ({ value }) => {
                //   return await emailValidator({ value })
                // },
              }}
              children={(field) => {
                return (
                  <form.Subscribe
                    selector={(state) => state.values.noEmail}
                    children={(noEmail) => (
                      <FormInput
                        field={field}
                        label="Email"
                        type="email"
                        id={field.name}
                        name={field.name}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        required={!noEmail}
                        disabled={!!noEmail}
                        className="w-full"
                      />
                    )}
                  />
                )
              }}
            />
          </div>
          <div className="flex items-center sm:ml-2 mt-1 sm:mt-0 w-auto h-full sm:self-center">
            <form.Field
              name="noEmail"
              validators={{
                onBlur: ({ value }) => {
                  const result = schema.shape.noEmail.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => {
                return (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={field.name}
                      checked={!!field.state.value}
                      onCheckedChange={(checked) => {
                        field.handleChange(Boolean(checked))
                        // If noEmail is checked, clear the email field and its errors
                        if (checked) {
                          form.setFieldValue('email', '')
                          form.setFieldMeta('email', (prev) => ({
                            ...prev,
                            errors: [],
                            errorMap: {},
                          }))
                        }
                      }}
                      className="h-5 w-5"
                    />
                    <Label
                      htmlFor={field.name}
                      className="font-medium whitespace-nowrap"
                    >
                      Member does not have an email address
                    </Label>
                  </div>
                )
              }}
            />
          </div>
        </div>

        <form.Field
          name="etransferEmail"
          validators={{
            onBlur: ({ value }) => {
              if (!value) return undefined
              const result = schema.shape.etransferEmail.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <form.Subscribe
                selector={(state) => [state.values.noEmail, state.values.email]}
                children={([noEmail, email]) => (
                  <FormInput
                    field={field}
                    label="E-Transfer Email"
                    type="email"
                    id={field.name}
                    name={field.name}
                    value={field.state.value ?? ''}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    disabled={!!noEmail}
                    placeholder={
                      noEmail ? '' : (email as string) || 'Same as email'
                    }
                    className="w-full"
                  />
                )}
              />
            )
          }}
        />

        <form.Field
          name="churchId"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.churchId.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <AutoCompleteSelect
                  field={field}
                  label="Church"
                  id={field.name}
                  name={field.name}
                  options={filteredChurches.map((church) => ({
                    value: church._id,
                    label: church.name,
                  }))}
                  required
                  addDialog={
                    <ChurchForm
                      type="add"
                      onClose={() => setOpenChurchDialog(false)}
                      open={openChurchDialog}
                      trigger={
                        <Button
                          onClick={() => {
                            setOpenChurchDialog(true)
                          }}
                        >
                          <Plus /> Add Church
                        </Button>
                      }
                    />
                  }
                />
              </div>
            )
          }}
        />
        <form.Field
          name="address"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.address.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <AddressInput
                  field={field}
                  label="Address"
                  onAddressSelect={(parsedAddress) => {
                    field.handleChange({
                      street: parsedAddress.street,
                      city: parsedAddress.city,
                      province: parsedAddress.province,
                      postal: parsedAddress.postal,
                    })
                  }}
                  placeholder="Start typing an address..."
                  required
                />
              </div>
            )
          }}
        />
        <form.Field
          name="phone"
          validators={{
            onBlur: ({ value }) => {
              if (!value) return undefined // optional
              const result = schema.shape.phone.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <PhoneInput
                  name={field.name}
                  label="Phone Number"
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors[0]}
                />
              </div>
            )
          }}
        />

        <form.Field
          name="familyMembers"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.familyMembers.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FamilyMembersSelector
                  field={field}
                  label="Family Members"
                  id={field.name}
                  name={field.name}
                  options={familyMembers.map((member) => ({
                    value: member._id,
                    label: member.name,
                  }))}
                />
              </div>
            )
          }}
        />

        <div className="grid grid-cols-2 gap-4 items-center">
          <div className={type === 'add' ? 'col-span-2' : 'col-span-1'}>
            <form.Field
              name="role"
              validators={{
                onBlur: ({ value }) => {
                  const result = schema.shape.role.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => {
                return (
                  <div className="gap-0">
                    <AutoCompleteSelect
                      field={field}
                      label="Role"
                      id={field.name}
                      name={field.name}
                      options={Object.entries(memberRoles).map(
                        ([key, role]) => ({
                          value: key,
                          label: role,
                        }),
                      )}
                    />
                  </div>
                )
              }}
            />
          </div>

          {type === 'edit' && (
            <form.Field
              name="active"
              validators={{
                onBlur: ({ value }) => {
                  const result = schema.safeParse({
                    ...form.state.values,
                    active: value,
                  })
                  return result.success
                    ? undefined
                    : result.error.errors.find((e) => e.path[0] === 'active')
                        ?.message
                },
              }}
              children={(field) => {
                return (
                  <div className="gap-0">
                    <Switch
                      field={field}
                      label="Is member active?"
                      id={field.name}
                      name={field.name}
                    />
                  </div>
                )
              }}
            />
          )}
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <>
              <LoadingButton
                type="submit"
                className="w-full mt-4"
                isLoading={isPending}
                disabled={!canSubmit || isPending}
              >
                {isPending ? loadingLabel : buttonLabel}
              </LoadingButton>
            </>
          )}
        />
      </form>
    </Modal>
  )
}
